import { apiClient } from './apiClient';

export interface JobApplicationStatusHistory {
  id: string;
  jobApplicationId?: string;
  statusName: string;
  stage?: number | null;
  stepName?: string | null;
  stepNumber?: number | null;
  notes?: string | null;
  createdAt: string;
}

export interface JobApplication {
  id: string;
  jobPostingId: string;
  jobPosting?: {
    id: string;
    jobTitle?: string;
    jobId?: string;
  };
  mobileNumber: string;
  email?: string;
  firstName: string;
  lastName: string;
  gender: string;
  dob: string;
  resumeFileName?: string;
  resumeUrl?: string;
  availability: {
    allDays: boolean;
    hours?: { from: string; to: string };
    days?: Array<{ day: string; hours: { from: string; to: string } }>;
  };
  availabilities?: Array<Record<string, unknown>>;
  status?: 'pending' | 'reviewed' | 'shortlisted' | 'rejected' | 'hired';
  statusHistory?: JobApplicationStatusHistory[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateJobApplicationDto {
  jobPostingId: string;
  mobileNumber: string;
  email?: string;
  firstName: string;
  lastName: string;
  gender: string;
  dob: string;
  resumeFileName?: string;
  availability: {
    allDays: boolean;
    hours?: { from: string; to: string };
    days?: Array<{ day: string; hours: { from: string; to: string } }>;
  };
}

class JobApplicationService {
  async createApplication(
    applicationData: CreateJobApplicationDto,
    resumeFile?: File
  ): Promise<JobApplication> {
    const formData = new FormData();
    
    // Append all application data fields
    formData.append('jobPostingId', applicationData.jobPostingId);
    formData.append('mobileNumber', applicationData.mobileNumber);
    if (applicationData.email) {
      formData.append('email', applicationData.email);
    }
    formData.append('firstName', applicationData.firstName);
    formData.append('lastName', applicationData.lastName);
    formData.append('gender', applicationData.gender);
    formData.append('dob', applicationData.dob);
    if (applicationData.resumeFileName) {
      formData.append('resumeFileName', applicationData.resumeFileName);
    }
    formData.append('availability', JSON.stringify(applicationData.availability));
    
    // Append resume file if provided
    if (resumeFile) {
      formData.append('resume', resumeFile);
    }
    
    const response = await apiClient.postFormData('/job-applications', formData);
    return response.data;
  }

  async getAllApplications(): Promise<JobApplication[]> {
    const response = await apiClient.get('/job-applications');
    return response.data;
  }

  async getApplicationById(id: string): Promise<JobApplication> {
    const response = await apiClient.get(`/job-applications/${id}`);
    return response.data;
  }

  async updateApplicationStatus(
    id: string,
    status: 'pending' | 'reviewed' | 'shortlisted' | 'rejected' | 'hired'
  ): Promise<JobApplication> {
    const response = await apiClient.patch(`/job-applications/${id}/status`, { status });
    return response.data;
  }

  async deleteApplication(id: string): Promise<void> {
    await apiClient.delete(`/job-applications/${id}`);
  }

  // Save method for job application (can be used for saving drafts or updates)
  async saveApplication(
    applicationData: CreateJobApplicationDto,
    resumeFile?: File
  ): Promise<JobApplication> {
    // This method uses the same create logic but can be extended for save/update functionality
    return this.createApplication(applicationData, resumeFile);
  }

  async getStatusHistory(applicationId: string): Promise<any[]> {
    const response = await apiClient.get(`/job-applications/${applicationId}/status-history`);
    return response.data;
  }

  async sendShortlistEmail(applicationId: string): Promise<void> {
    await apiClient.post(`/job-applications/${applicationId}/shortlist-email`);
  }

  async getApplicationByContact(mobileOrEmail: string): Promise<JobApplication> {
    const response = await apiClient.get(`/job-applications/by-contact/${encodeURIComponent(mobileOrEmail)}`);
    return response.data;
  }
}

export const jobApplicationService = new JobApplicationService();

